
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CertificatesTable } from './CertificatesTable';
import { CertificateTrackingDashboard } from './CertificateTrackingDashboard';
import { CertificateFilters } from './CertificateFilters';
import { Button } from '@/components/ui/button';
import { ImportCertificateNumbersDialog } from './ImportCertificateNumbersDialog';
import { FilePlus, ListFilter, ChartBar, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

export function CertificatesListContent() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  
  // Add filter state
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);
  const [courseTypeFilter, setCourseTypeFilter] = useState<string | undefined>(undefined);
  const [reportingStatusFilter, setReportingStatusFilter] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Add refresh function
  const handleRefresh = () => {
    console.log('Refreshing certificate list');
    // Add any refresh logic here
  };

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <h1 className="text-xl md:text-2xl font-bold">Certificates</h1>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size={isMobile ? "sm" : "default"}
            onClick={() => navigate('/certificates/manage')}
          >
            Manage Types
          </Button>
          
          <Button 
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => navigate('/certificates/templates')}
          >
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </Button>
          
          <Button 
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => navigate('/certificates/reports')}
          >
            Reports
          </Button>
          
          <Button 
            size={isMobile ? "sm" : "default"}
            onClick={() => setIsImportDialogOpen(true)}
            className="flex-grow md:flex-grow-0"
          >
            <FilePlus className="h-4 w-4 mr-2" />
            Import Certificate Numbers
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full md:w-[400px]">
          <TabsTrigger value="list" className="flex items-center gap-2">
            <ListFilter className="h-4 w-4" />
            Certificate List
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <ChartBar className="h-4 w-4" />
            Tracking Dashboard
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Certificate Management</CardTitle>
              <CardDescription>
                View, search and manage all certificates in the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CertificateFilters
                stateFilter={stateFilter}
                setStateFilter={setStateFilter}
                courseTypeFilter={courseTypeFilter}
                setCourseTypeFilter={setCourseTypeFilter}
                reportingStatusFilter={reportingStatusFilter}
                setReportingStatusFilter={setReportingStatusFilter}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onRefresh={handleRefresh}
              />
              
              <div className="mt-6">
                <ResponsiveTable compact={isMobile}>
                  <CertificatesTable searchTerm={searchQuery} />
                </ResponsiveTable>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="dashboard" className="mt-6">
          <CertificateTrackingDashboard />
        </TabsContent>
      </Tabs>
      
      <ImportCertificateNumbersDialog 
        open={isImportDialogOpen} 
        onOpenChange={setIsImportDialogOpen}
      />
    </div>
  );
}
